import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { ChartWrapper } from "components/charts";
import { ComboChart } from "components/charts/ComboChart/ComboChart";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import {
  createDateRange,
  noData,
  validBarData,
  validLineData,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import AccountsTable from "./components/AccountsTable";
import EnterpriseTable from "./components/EnterpriseTable";
import {
  fetchPeopleOverviewLineCharts,
  fetchPeopleOverviewStatistics,
  sharedOverviewLineProps,
  yearDateRange,
} from "./utils";
import moment from "moment";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";

import styles from "./peopleOverviewReport.module.scss";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../peopleStyles.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import ReportingExport from "components/reportingExport/ReportingExport";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import useHasAddOns from "features/Library/Common/Hooks/useHasAddOns";
import { AddOnIds } from "enums";

export type Props = PropsFromRedux & {
  scrollToTop: () => void;
  usesDropdownMenuNavigation?: boolean;
  renderHeader?: () => JSX.Element | null;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
};

const dateRange = yearDateRange();

// Remove account id once data team is finished with bsi table.
export const PeopleOverviewReport = ({
  scrollToTop,
  accountId,
  accountType,
  usesDropdownMenuNavigation,
  renderHeader,
  childAccountId,
  hasCreateAccountsAddOn,
}: Props) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType);
  const hasCreateAccountsOrMarketplace = useHasAddOns(accountId, [AddOnIds.CreateAccounts, AddOnIds.Marketplace], "or");

  const initialOverrides = useMemo(() => {
    if (usesDropdownMenuNavigation && childAccountId) {
      return { accounts: [childAccountId] as number[], includeMyData: false };
    }
    return undefined;
  }, [usesDropdownMenuNavigation, childAccountId]);

  const [filter, setFilter] = usePerformanceFilter({
    initialDateRange: dateRange,
    includeAccountsDropdown: accountFilterShouldShow,
    accountId,
    initialOverrides,
    supportsSPType: true,
  });

  // Remove account id once data team is finished with bsi table.
  const [selectedAccountId, setSelectedAccountId] = useState<number | null | undefined>(undefined);

  const dateRangeForLineChart = useMemo(
    () => createDateRange(filter.dateFrom, filter.dateTo),
    [filter.dateFrom, filter.dateTo],
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRangeForLineChart,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const { registerBreadcrumb, hasBreadcrumbsToDisplay, domElements } = usePerformanceBreadcrumbs();

  const handleAccountSelect = useCallback(
    (id: number, name: string) => {
      setSelectedAccountId(id);
      registerBreadcrumb({ text: name, action: () => setSelectedAccountId(null) });
      scrollToTop();
    },
    [registerBreadcrumb, scrollToTop],
  );

  useEffect(() => {
    if (usesDropdownMenuNavigation) {
      setSelectedAccountId(childAccountId);
    } else if (accountFilterShouldShow) {
      setSelectedAccountId(null);
    } else {
      setSelectedAccountId(accountId);
    }
  }, [usesDropdownMenuNavigation, accountFilterShouldShow, accountId, childAccountId]);

  const customerFilter: PerformanceWithAccountFilter | null = useMemo(() => {
    if (selectedAccountId === null || selectedAccountId === undefined) {
      return null;
    }

    const isOwnAccount = selectedAccountId === accountId;

    return {
      ...filter,
      accounts: isOwnAccount ? [] : [selectedAccountId],
      includeMyData: isOwnAccount,
    };
  }, [selectedAccountId, accountId, filter]);

  const activeFilter = useMemo(() => customerFilter ?? filter, [customerFilter, filter]);

  const lineChartQuery = useQuery({
    queryFn: fetchPeopleOverviewLineCharts,
    queryKey: ["PeopleOverviewLineCharts", { ...activeFilter, aggregation: chartPeriod }],
    enabled: chartPeriod !== CHART_PERIOD.UNSET && activeFilter.type !== undefined,
  });

  const cardsQuery = useQuery({
    queryFn: fetchPeopleOverviewStatistics,
    queryKey: ["PeopleOverviewStatistics", activeFilter],
    enabled: activeFilter.type !== undefined,
  });

  const tooltipFormatter = useCallback(
    (x: Date, y: number, index: number) => {
      if (lineChartQuery.data) {
        return lineChartQuery.data.activeUsersTooltips[index];
      }

      return `${moment(x).format("MMM 'YY")}: ${roundToTwoDigits(y)} Users`;
    },
    [lineChartQuery.data],
  );

  const ignoreInCountAndHide = useMemo(() => {
    const ignore = ["type"];
    if (hasBreadcrumbsToDisplay || usesDropdownMenuNavigation) {
      ignore.push("accounts", "includeMyData");
    }

    return ignore;
  }, [hasBreadcrumbsToDisplay, usesDropdownMenuNavigation]);

  const headerContent = useMemo<ReactNode>(() => {
    if (hasBreadcrumbsToDisplay) {
      return <div className="breadCrumbs">{domElements}</div>;
    }

    if (usesDropdownMenuNavigation && renderHeader) {
      return renderHeader();
    }

    return <h2 className="performanceTitle">Summary</h2>;
  }, [hasBreadcrumbsToDisplay, domElements, usesDropdownMenuNavigation, renderHeader]);

  return (
    <div className="performanceRoot">
      <div className="performanceHeader peopleActivityHeader">
        <div className={"performanceHeader peopleActivityFilters"}>
          {headerContent}
          {accountFilterShouldShow && (
            <div>
              <ReportingFilter
                currentFilter={filter}
                callback={setFilter}
                includeAccounts={false}
                includeAccountsDropdown={accountFilterShouldShow}
                initialDateRange={yearDateRange()}
                ignoreInCountAndHide={ignoreInCountAndHide}
                accountsLabel={hasCreateAccountsOrMarketplace ? "Customers" : "Accounts"}
                supportsSPType
              />
            </div>
          )}
        </div>
        {accountFilterShouldShow && <ReportingExport />}
      </div>

      <div className={styles.comboChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={"Monthly Active Users vs Interactions"}
          showLegend
          legendLabels={["Active Users", "Interactions"]}
          colorRange={[reportingColors.blue, reportingColors.yellow]}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isFetched &&
            (validLineData(lineChartQuery.data!.activeUsers.yData) ||
              validBarData(lineChartQuery.data?.interactionsAsBarData!)) ? (
              <ComboChart
                {...sharedAccountReportingLineProps}
                xData={lineChartQuery.data!.activeUsers.xData[0]}
                lineYData={lineChartQuery.data!.activeUsers.yData[0]}
                barYData={lineChartQuery.data!.interactionsAsBarData}
                lineTooltipFormatter={tooltipFormatter}
                {...sharedOverviewLineProps}
              />
            ) : (
              noData(activeFilter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className="tabSpacer">
        <RequestStatusRenderer state={cardsQuery.status}>
          <CardReporting items={cardsQuery.data!} />
        </RequestStatusRenderer>
      </div>

      {selectedAccountId !== undefined && (
        <>
          {selectedAccountId === null ? (
            <AccountsTable filter={filter} handleAccountSelection={handleAccountSelect} />
          ) : (
            <EnterpriseTable
              includeHyperlink={
                (accountFilterShouldShow && (filter.accounts?.length ?? 0) > 0) || selectedAccountId === accountId
              }
              filter={activeFilter}
              childAccountId={childAccountId ?? selectedAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
            />
          )}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
  };
};

const mapDispatchToProps = (_dispatch: AppDispatch) => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PeopleOverviewReport);
export default ConnectedComponent;
